import { useEffect, useMemo, useState } from 'react';
import {
	useDeleteFertilizerPack,
	useGetFertilizers,
	usePostFertilizerPack,
	useUpdateFertilizerPack,
	type FertilizerPack,
	type FertilizerPackPayload,
} from '@practium/features/api';
import { useToast } from '@practium/ui';
import { FERTILIZER_PACK_SLOTS } from '../constants/maintenance.constants';
import { FertilizerPackSchema } from '../schemas/fertilizerPack.schema';

export type FertilizerPackModalMode = 'add' | 'edit' | 'view';

type PackSlotKey = (typeof FERTILIZER_PACK_SLOTS)[number]['key'];

const emptyForm = (): FertilizerPackPayload => ({
	name: '',
	mix: '',
	N: '',
	P2O5: '',
	K2O: '',
	Ca: '',
	Mg: '',
	S: '',
	B: '',
	Zn: '',
	Cu: '',
	Mn: '',
	Fe: '',
});

const fromPack = (pack?: FertilizerPack | null): FertilizerPackPayload => {
	if (!pack) return emptyForm();
	const slotValue = (value?: string | null) => (value ? String(value) : '');
	return {
		...emptyForm(),
		name: pack.name ?? '',
		mix: slotValue(pack.mix),
		N: slotValue(pack.N),
		P2O5: slotValue(pack.P2O5),
		K2O: slotValue(pack.K2O),
		Ca: slotValue(pack.Ca),
		Mg: slotValue(pack.Mg),
		S: slotValue(pack.S),
		B: slotValue(pack.B),
		Zn: slotValue(pack.Zn),
		Cu: slotValue(pack.Cu),
		Mn: slotValue(pack.Mn),
		Fe: slotValue(pack.Fe),
	};
};

const toSchemaForm = (form: FertilizerPackPayload) => ({
	name: form.name ?? '',
	mix: form.mix ? String(form.mix) : '',
	N: form.N ? String(form.N) : '',
	P2O5: form.P2O5 ? String(form.P2O5) : '',
	K2O: form.K2O ? String(form.K2O) : '',
	Ca: form.Ca ? String(form.Ca) : '',
	Mg: form.Mg ? String(form.Mg) : '',
	S: form.S ? String(form.S) : '',
	B: form.B ? String(form.B) : '',
	Zn: form.Zn ? String(form.Zn) : '',
	Cu: form.Cu ? String(form.Cu) : '',
	Mn: form.Mn ? String(form.Mn) : '',
	Fe: form.Fe ? String(form.Fe) : '',
});

interface UseFertilizerPackFormOptions {
	isOpen: boolean;
	mode: FertilizerPackModalMode;
	pack?: FertilizerPack | null;
	onClose: () => void;
	onSaved: (pack: FertilizerPack) => void;
	onDeleted?: () => void;
}

export const useFertilizerPackForm = ({
	isOpen,
	mode,
	pack,
	onClose,
	onSaved,
	onDeleted,
}: UseFertilizerPackFormOptions) => {
	const toast = useToast();
	const isView = mode === 'view';
	const [form, setForm] = useState<FertilizerPackPayload>(emptyForm);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const { data: fertilizers = [] } = useGetFertilizers({ limit: 300 });
	const { mutateAsync: createPack, isPending: isCreating } = usePostFertilizerPack();
	const { mutateAsync: updatePack, isPending: isUpdating } = useUpdateFertilizerPack();
	const { mutateAsync: deletePack, isPending: isDeleting } = useDeleteFertilizerPack();

	useEffect(() => {
		if (!isOpen) return;
		setForm(fromPack(pack));
		setErrors({});
	}, [isOpen, pack]);

	const mixFertilizers = useMemo(() => {
		const mix = fertilizers.filter((item) => item.category === 'fertilizer');
		return mix.length > 0 ? mix : fertilizers;
	}, [fertilizers]);

	const optionsBySlot = useMemo(() => {
		return Object.fromEntries(
			FERTILIZER_PACK_SLOTS.map((slot) => {
				const list =
					slot.key === 'mix'
						? mixFertilizers
						: mixFertilizers.filter((item) => {
								const nutrient = slot.key as Exclude<PackSlotKey, 'mix'>;
								return Number(item[nutrient] ?? 0) > 1;
							});
				const source = list.length > 0 ? list : mixFertilizers;
				return [
					slot.key,
					source.map((item) => ({ value: item._id, label: item.name })),
				];
			})
		) as Record<PackSlotKey, Array<{ value: string; label: string }>>;
	}, [mixFertilizers]);

	const set = (key: keyof FertilizerPackPayload, value: string) => {
		setForm((current) => ({ ...current, [key]: value }));
		setErrors((current) => ({ ...current, [key]: '' }));
	};

	const handleSubmit = async () => {
		if (isView) {
			onClose();
			return;
		}

		const result = FertilizerPackSchema.safeParse(toSchemaForm(form));
		if (!result.success) {
			const fieldErrors: Record<string, string> = {};
			result.error.issues.forEach((issue) => {
				const path = String(issue.path[0] ?? '');
				if (path) fieldErrors[path] = issue.message;
			});
			setErrors(fieldErrors);
			return;
		}

		const payload: FertilizerPackPayload = { name: result.data.name };
		FERTILIZER_PACK_SLOTS.forEach(({ key }) => {
			const value = result.data[key];
			payload[key] = value ? String(value) : null;
		});

		try {
			const packId = pack?._id ? String(pack._id) : '';
			const saved =
				mode === 'edit' && packId
					? await updatePack({ id: packId, payload })
					: await createPack(payload);
			if (!saved?._id) {
				throw new Error('Erro ao salvar o pacote.');
			}
			onSaved(saved);
			onClose();
		} catch (submitError) {
			setErrors({
				name: submitError instanceof Error ? submitError.message : 'Erro ao salvar o pacote.',
			});
		}
	};

	const handleDelete = async () => {
		const packId = pack?._id ? String(pack._id) : '';
		if (!packId) return;
		try {
			await deletePack(packId);
			onDeleted?.();
			onClose();
			toast.success('Pacote excluído com sucesso.');
		} catch (deleteError) {
			setErrors({
				name: deleteError instanceof Error ? deleteError.message : 'Erro ao excluir o pacote.',
			});
		}
	};

	const slotLabel = (key: PackSlotKey, value?: string | null) =>
		optionsBySlot[key].find((option) => option.value === value)?.label || '—';

	return {
		form,
		errors,
		set,
		isView,
		isLoading: isCreating || isUpdating || isDeleting,
		optionsBySlot,
		slotLabel,
		handleSubmit,
		handleDelete,
	};
};
